/**
 * AppRoutes — the full route table for the application.
 *
 * Extracted from App.js to keep that file focused on provider composition.
 * All non-LCP routes are code-split via React.lazy().
 */
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy } from "react";

import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import { audienceConfigs } from "@/pages/audienceConfigs";

// Marketing / static
const About             = lazy(() => import("@/pages/About"));
const Network           = lazy(() => import("@/pages/Network"));
const Solutions         = lazy(() => import("@/pages/Solutions"));
const MergedPage        = lazy(() => import("@/components/MergedPage"));
const Explore           = lazy(() => import("@/pages/Explore"));
const ObjectProfile     = lazy(() => import("@/pages/ObjectProfile"));
const AdminHealth       = lazy(() => import("@/pages/AdminHealth"));
const HubsLibrary       = lazy(() => import("@/pages/HubsLibrary"));
const Marketplace       = lazy(() => import("@/pages/Marketplace"));
const Foundation        = lazy(() => import("@/pages/Foundation"));
const Academy           = lazy(() => import("@/pages/Academy"));
const Contact           = lazy(() => import("@/pages/Contact"));
const Credits           = lazy(() => import("@/pages/Credits"));
const Legal             = lazy(() => import("@/pages/Legal"));
const NotFound          = lazy(() => import("@/pages/NotFound"));

// Auth
const Login             = lazy(() => import("@/pages/Login"));
const Register          = lazy(() => import("@/pages/Register"));
const ForgotPassword    = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword     = lazy(() => import("@/pages/ResetPassword"));

// AI engines
const AiToolsHub          = lazy(() => import("@/pages/ai/AiToolsHub"));
const WasteToResource     = lazy(() => import("@/pages/ai/WasteToResource"));
const HubBuilder          = lazy(() => import("@/pages/ai/HubBuilder"));
const OpportunityExplorer = lazy(() => import("@/pages/ai/OpportunityExplorer"));
const DigitalTwin         = lazy(() => import("@/pages/ai/DigitalTwin"));
const Calculators         = lazy(() => import("@/pages/ai/Calculators"));

// Maps / Intelligence
const Atlas                  = lazy(() => import("@/pages/Atlas"));
const Intelligence           = lazy(() => import("@/pages/Intelligence"));
const IntelligenceMarkets    = lazy(() => import("@/pages/IntelligenceMarkets"));
const IntelligenceList       = lazy(() => import("@/pages/IntelligenceList"));
const IntelligenceCapital    = lazy(() => import("@/pages/IntelligenceCapital"));
const IntelligencePassport   = lazy(() => import("@/pages/IntelligencePassport"));
const IntelligenceGraphQL    = lazy(() => import("@/pages/IntelligenceGraphQL"));
const IndexDetail            = lazy(() => import("@/pages/IndexDetail"));
const MethodologyCenter      = lazy(() => import("@/pages/MethodologyCenter"));

// Investor / portal
const InvestorPortal    = lazy(() => import("@/pages/InvestorPortal"));
const InvestorPrefs     = lazy(() => import("@/pages/InvestorPrefs"));
const KYC               = lazy(() => import("@/pages/KYC"));
const EngineHistory     = lazy(() => import("@/pages/EngineHistory"));
const UserDashboard     = lazy(() => import("@/pages/UserDashboard"));
const FoundationApply   = lazy(() => import("@/pages/FoundationApply"));

// Network proof + announcements
const NetworkProof       = lazy(() => import("@/pages/NetworkProof"));
const AnnouncementDetail = lazy(() => import("@/pages/AnnouncementDetail"));
const MACCIA             = lazy(() => import("@/pages/MACCIA"));
const PeopleNetwork      = lazy(() => import("@/pages/PeopleNetwork"));
const AssetExchange      = lazy(() => import("@/pages/AssetExchange"));
const Community          = lazy(() => import("@/pages/Community"));

// Other
const DPP     = lazy(() => import("@/pages/DPP"));
const Billing = lazy(() => import("@/pages/Billing"));

// Admin
const Admin              = lazy(() => import("@/pages/Admin"));
const AdminPeople        = lazy(() => import("@/pages/AdminPeople"));
const AdminIntelligence  = lazy(() => import("@/pages/AdminIntelligence"));
const AdminDesignSystem  = lazy(() => import("@/pages/AdminDesignSystem"));

/** Legacy /explore/:type[/id] → /intelligence/explore/:type[/id] */
function NavigateExploreLegacy() {
  const { type, id } = useParams();
  const path = ["/intelligence/explore", type, id].filter(Boolean).join("/");
  return <Navigate to={path} replace />;
}

const marketplaceTabs = {
  marketplace: { label: "Marketplace",    sub: "Have / Need · matched",     component: Marketplace   },
  assets:      { label: "Asset Exchange", sub: "Physical asset listings",   component: AssetExchange },
};

const foundationTabs = {
  foundation: { label: "Foundation", sub: "Programs · grants",         component: Foundation    },
  people:     { label: "People",     sub: "Operators · entrepreneurs", component: PeopleNetwork },
  community:  { label: "Community",  sub: "Villages · participants",   component: Community     },
};

const dppTabs = {
  passports: { label: "Product Passports", sub: "Digital provenance",       component: DPP     },
  credits:   { label: "Credits",           sub: "Verified outcomes ledger", component: Credits },
};

const intelligenceTabs = {
  platform:    { label: "Intelligence Platform", sub: "Overview · KPIs",      component: Intelligence      },
  methodology: { label: "Methodology",           sub: "How scores are built", component: MethodologyCenter },
};

const exploreTabs = {
  graph: { label: "Knowledge Graph", sub: "Nodes · edges · matches", component: Explore },
  atlas: { label: "Atlas",           sub: "Geographic map view",     component: Atlas   },
};

export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/network" element={<Network />} />

        <Route path="/hubs" element={<HubsLibrary />} />
        <Route path="/marketplace" element={
          <MergedPage defaultKey="marketplace" testId="marketplace-tabs" tabs={marketplaceTabs} />
        } />
        <Route path="/foundation" element={
          <MergedPage defaultKey="foundation" testId="foundation-tabs" tabs={foundationTabs} />
        } />
        <Route path="/academy" element={<Academy />} />
        <Route path="/contact" element={<Contact />} />

        <Route path="/ai-tools" element={<AiToolsHub />} />
        <Route path="/ai-tools/waste-to-resource" element={<WasteToResource />} />
        <Route path="/ai-tools/hub-builder" element={<HubBuilder />} />
        <Route path="/ai-tools/opportunity-explorer" element={<OpportunityExplorer />} />
        <Route path="/ai-tools/digital-twin" element={<DigitalTwin />} />
        <Route path="/ai-tools/calculators" element={<Calculators />} />

        <Route path="/solutions" element={<Solutions />} />

        {/* Old audience routes redirect into /solutions tab strip */}
        {Object.keys(audienceConfigs).map((slug) => (
          <Route
            key={slug}
            path={`/for/${slug}`}
            element={<Navigate to={`/solutions?audience=${slug}`} replace />}
          />
        ))}

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/portal" element={<ProtectedRoute><InvestorPortal /></ProtectedRoute>} />
        <Route path="/portal/kyc" element={<ProtectedRoute><KYC /></ProtectedRoute>} />
        <Route path="/portal/history" element={<ProtectedRoute><EngineHistory /></ProtectedRoute>} />
        <Route path="/portal/preferences" element={<ProtectedRoute><InvestorPrefs /></ProtectedRoute>} />

        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/admin/health" element={<ProtectedRoute><AdminHealth /></ProtectedRoute>} />
        <Route path="/admin/people" element={<ProtectedRoute><AdminPeople /></ProtectedRoute>} />
        <Route path="/admin/intelligence" element={<ProtectedRoute><AdminIntelligence /></ProtectedRoute>} />
        <Route path="/admin/design-system" element={<ProtectedRoute><AdminDesignSystem /></ProtectedRoute>} />

        <Route path="/credits" element={<Navigate to="/dpp?view=credits" replace />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/plans" element={<Billing />} />
        <Route path="/dpp" element={
          <MergedPage defaultKey="passports" testId="dpp-tabs" tabs={dppTabs} />
        } />
        <Route path="/dpp/:id" element={<DPP />} />

        <Route path="/atlas" element={<Navigate to="/intelligence/explore?view=atlas" replace />} />
        <Route path="/intelligence" element={
          <MergedPage defaultKey="platform" testId="intelligence-tabs" tabs={intelligenceTabs} />
        } />
        <Route path="/intelligence/markets" element={<IntelligenceMarkets />} />
        <Route path="/intelligence/indexes/:id" element={<IndexDetail />} />
        <Route path="/intelligence/explore" element={
          <MergedPage defaultKey="graph" testId="explore-tabs" tabs={exploreTabs} />
        } />
        <Route path="/intelligence/explore/:type" element={<Explore />} />
        <Route path="/intelligence/explore/:type/:id" element={<Explore />} />
        <Route path="/intelligence/communities" element={<IntelligenceList type="community" />} />
        <Route path="/intelligence/community/:id" element={<IntelligencePassport type="community" />} />
        <Route path="/intelligence/materials" element={<IntelligenceList type="material" />} />
        <Route path="/intelligence/material/:id" element={<IntelligencePassport type="material" />} />
        <Route path="/intelligence/technologies" element={<IntelligenceList type="technology" />} />
        <Route path="/intelligence/technology/:id" element={<IntelligencePassport type="technology" />} />
        <Route path="/intelligence/capital" element={<IntelligenceCapital />} />
        <Route path="/intelligence/capital/passport/:id" element={<IntelligencePassport type="capital" />} />
        <Route path="/intelligence/capital/:slug" element={<IntelligenceCapital />} />
        <Route path="/intelligence/opportunities" element={<IntelligenceList type="opportunity" />} />
        <Route path="/intelligence/opportunity/:id" element={<IntelligencePassport type="opportunity" />} />
        <Route path="/intelligence/graphql" element={<IntelligenceGraphQL />} />
        <Route path="/intelligence/asset/:id" element={<ObjectProfile type="asset" />} />
        <Route path="/methodology" element={<Navigate to="/intelligence?view=methodology" replace />} />

        <Route path="/network-proof" element={<NetworkProof />} />
        <Route path="/network-proof/maccia" element={<MACCIA />} />
        <Route path="/network-proof/:id" element={<AnnouncementDetail />} />

        <Route path="/people" element={<Navigate to="/foundation?view=people" replace />} />
        <Route path="/community" element={<Navigate to="/foundation?view=community" replace />} />
        <Route path="/asset-exchange" element={<Navigate to="/marketplace?view=assets" replace />} />
        <Route path="/explore" element={<Navigate to="/intelligence/explore" replace />} />
        <Route path="/explore/:type" element={<NavigateExploreLegacy />} />
        <Route path="/explore/:type/:id" element={<NavigateExploreLegacy />} />

        <Route path="/partners/:id" element={<ObjectProfile type="partner" />} />
        <Route path="/people/:id" element={<ObjectProfile type="person" />} />
        <Route path="/dashboard" element={<ProtectedRoute><UserDashboard /></ProtectedRoute>} />

        <Route path="/circularity-station-operating-system" element={<Navigate to="/network?view=station" replace />} />
        <Route path="/circularity-hub-operating-system" element={<Navigate to="/network?view=hub" replace />} />
        <Route path="/foundation/apply" element={<FoundationApply />} />

        <Route path="/legal/terms" element={<Legal kind="terms" />} />
        <Route path="/legal/privacy" element={<Legal kind="privacy" />} />
        <Route path="/legal/risk-disclaimer" element={<Legal kind="risk-disclaimer" />} />
        <Route path="/legal/cookies" element={<Legal kind="cookies" />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
